// My schedule, the teacher's whole UI (SPEC §7.9): phone-first list of the next 14 days, or a week grid.

import type { ApiResult } from "../../api/client";
import { fetchMyLessons, fetchMyWeek } from "../../api/me";
import type { MyDay, MyLesson, MyWeek } from "../../api/types";
import { addDays, mondayOf } from "../../dates";
import { formatDate, t } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { requireSchool } from "../../session";
import { buildDateNavigator } from "../../ui/dateNavigator";
import { createButton, createElement } from "../../ui/dom";
import { toTimelineBlock } from "../../ui/gridColumns";
import { buildTimeline, type TimelineColumn, type TimelineLesson } from "../../ui/timeline";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { describeLabels, describeStateClass } from "./myLessonCard";
import { buildMyScheduleList } from "./myScheduleList";

const UPCOMING_DAYS: number = 14;
const NARROW_SCREEN_PIXELS: number = 640;

export async function renderMySchedulePage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const view: string = route.query.get("view") ?? (window.innerWidth < NARROW_SCREEN_PIXELS ? "list" : "grid");
  const page: HTMLElement = startPage(container, t("mySchedule.title"));
  page.append(createLoading());
  if (view === "list") {
    await renderList(container, page);
    return;
  }

  await renderGrid(container, page, mondayOf(route.query.get("from") ?? requireSchool().today));
}

async function renderList(container: HTMLElement, page: HTMLElement): Promise<void> {
  const today: string = requireSchool().today;
  const lessons: ApiResult<MyLesson[]> = await fetchMyLessons(today, addDays(today, UPCOMING_DAYS - 1));
  if (lessons.ok === false) {
    showPageProblem(page, lessons.problem);
    return;
  }

  const draw = (): void => {
    const fresh: HTMLElement = startPage(container, t("mySchedule.title"));
    fresh.append(buildViewSwitch("list"), createElement("h2", "", t("mySchedule.upcoming")),
      buildMyScheduleList(lessons.data, today));
  };
  setRedraw(container, draw);
  draw();
}

async function renderGrid(container: HTMLElement, page: HTMLElement, from: string): Promise<void> {
  const week: ApiResult<MyWeek> = await fetchMyWeek(from);
  if (week.ok === false) {
    showPageProblem(page, week.problem);
    return;
  }

  const draw = (): void => {
    const fresh: HTMLElement = startPage(container, t("mySchedule.title"));
    fresh.append(buildViewSwitch("grid"), buildDateNavigator(week.data.from, 7, (date: string): void => {
      navigate(`/my-schedule?view=grid&from=${mondayOf(date)}`);
    }), buildTimeline(week.data.days.map(toColumn)));
  };
  setRedraw(container, draw);
  draw();
}

function toColumn(day: MyDay): TimelineColumn {
  return {
    title: formatDate(day.date),
    titleRoute: null,
    lessons: day.lessons.map((lesson: MyLesson): TimelineLesson => ({
      range: lesson.range,
      title: lesson.groupName,
      lines: [lesson.classroomName, lesson.centerName],
      labels: describeLabels(lesson),
      stateClass: describeStateClass(lesson),
      route: null,
    })),
    unavailable: day.unavailable.map(toTimelineBlock),
  };
}

/** List / Week toggle, available on any screen (SPEC §7.9). */
function buildViewSwitch(current: "list" | "grid"): HTMLElement {
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  const list: HTMLButtonElement = createButton(t("mySchedule.list"), (): void => {
    navigate("/my-schedule?view=list");
  });
  const grid: HTMLButtonElement = createButton(t("mySchedule.grid"), (): void => {
    navigate("/my-schedule?view=grid");
  });
  list.setAttribute("aria-pressed", String(current === "list"));
  grid.setAttribute("aria-pressed", String(current === "grid"));
  toolbar.append(list, grid);
  return toolbar;
}
